import { useMutation, type MutationStatus } from "@tanstack/react-query";
import { AxiosError, type AxiosInstance } from "axios";
import { useCallback, useEffect } from "react";
import useApiClient from "./useApiClient";

type Requester = AxiosInstance["request"] & Omit<AxiosInstance, "request">;

interface Options {
  onStatusChange?: (status: MutationStatus) => void;
  onError?: (error: AxiosError, ...args: unknown[]) => void;
}

function useRequestCallback<A extends unknown[], R = unknown>(
  asyncMutator: (client: Requester, ...args: A) => Promise<R>,
  deps: ReadonlyArray<unknown>,
  options: Options = {},
) {
  const mutation = useMutation({
    mutationFn: ({ request, args }: { request: Requester; args: A }) => asyncMutator(request, ...args),
    onError: options.onError,
  });

  useEffect(() => {
    if (options.onStatusChange) {
      options.onStatusChange(mutation.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mutation.status]);

  const client = useApiClient();
  return useCallback(async (...args: A) => {
    const request = ((...p) => client.request(...p)) as AxiosInstance["request"] & Partial<Requester>;
    (Object.keys(client) as Array<keyof AxiosInstance>).forEach((k) => {
      if (k === "request") return;
      // @ts-expect-error: this is fine :)
      request[k] = client[k];
    });

    return mutation.mutateAsync({ request, args } as { request: Requester; args: A });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

export default useRequestCallback;
